import { Button, Card, CardBody, CardHeader, FormField, Surface } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEVICE_ALARMS } from '@warehouse/shared';
import { ArrowLeft, Power, Tag } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { getDevice, getDeviceHistory, leaseLpnRange, updateDevice } from '../api';
import { BatteryCell, DeviceAlarms, DeviceStatusBadge } from '../components/StatusBadges';
import { useConfirm } from '../../../components/ConfirmDialog';
import { FullPageSpinner } from '../../../components/FullPageSpinner';
import { PageHeader } from '../../../components/PageHeader';
import { formatDateTime, formatInteger, formatRelative } from '../../../lib/format';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';

/**
 * One scanner: its last heartbeat, its LPN lease and its history.
 *
 * The LPN lease is the operationally interesting part — a device spends its
 * block offline, so "how many numbers are left" decides whether a picker can
 * still label pallets in an aisle with no wifi (ARCHITECTURE §6).
 */
export function DeviceDetailPage(): ReactNode {
  const { t } = useTranslation(['devicesPrinting', 'common']);
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [name, setName] = useState('');

  const device = useQuery({ queryKey: ['device', id], queryFn: () => getDevice(id), enabled: Boolean(id) });
  const history = useQuery({
    queryKey: ['device-history', id],
    queryFn: () => getDeviceHistory(id),
    enabled: Boolean(id),
  });
  useLiveQuery(['devices'], ['device', id], { filter: (event) => event.entityId === id });

  useEffect(() => {
    if (device.data) setName(device.data.name);
  }, [device.data]);

  const save = useMutation({
    mutationFn: (patch: { name?: string; isActive?: boolean }) => updateDevice(id, patch),
    onSuccess: () => {
      toastSuccess(t('devicesPrinting:devices.toast.saved'));
      void queryClient.invalidateQueries({ queryKey: ['device', id] });
      void queryClient.invalidateQueries({ queryKey: ['device-history', id] });
      void queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const lease = useMutation({
    mutationFn: () => leaseLpnRange(id, true),
    onSuccess: (result) => {
      toastSuccess(
        t('devicesPrinting:devices.toast.leased', { first: result.firstLpn, last: result.lastLpn }),
      );
      void queryClient.invalidateQueries({ queryKey: ['device', id] });
      void queryClient.invalidateQueries({ queryKey: ['device-history', id] });
    },
    onError: (error) => {
      toastError(error);
    },
  });

  if (device.isLoading) return <FullPageSpinner />;
  if (!device.data) return null;
  const d = device.data;
  const lpnLow =
    d.lpnRemaining !== null && d.lpnRemaining <= DEVICE_ALARMS.lpnRemainingWarning;

  return (
    <>
      {dialog}
      <PageHeader
        title={t('devicesPrinting:devices.detail.title', { serial: d.serial })}
        subtitle={d.model ?? undefined}
        breadcrumb={
          <Link to="/devices/scanners" className="inline-flex items-center gap-1 hover:text-ink">
            <ArrowLeft className="size-3" aria-hidden />
            {t('devicesPrinting:devices.title')}
          </Link>
        }
        actions={
          <Button
            variant={d.isActive ? 'danger' : 'secondary'}
            icon={<Power />}
            loading={save.isPending}
            onClick={() => {
              void (async () => {
                if (d.isActive) {
                  const ok = await confirm({
                    title: t('devicesPrinting:devices.confirm.deactivateTitle'),
                    message: t('devicesPrinting:devices.confirm.deactivateMessage'),
                    confirmLabel: t('devicesPrinting:devices.actions.deactivate'),
                  });
                  if (!ok) return;
                }
                save.mutate({ isActive: !d.isActive });
              })();
            }}
          >
            {d.isActive
              ? t('devicesPrinting:devices.actions.deactivate')
              : t('devicesPrinting:devices.actions.activate')}
          </Button>
        }
      />

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <h2 className="font-display text-base font-semibold text-ink">
              {t('devicesPrinting:devices.detail.overview')}
            </h2>
          </CardHeader>
          <CardBody className="flex flex-col gap-4">
            <Row label={t('devicesPrinting:devices.columns.status')}>
              <DeviceStatusBadge status={d.status} />
            </Row>
            <Row label={t('devicesPrinting:devices.columns.alarms')}>
              <DeviceAlarms alarms={d.alarms} />
            </Row>
            <Row label={t('devicesPrinting:devices.columns.lastSeen')}>
              {d.lastSeenAt
                ? `${formatDateTime(d.lastSeenAt)} · ${formatRelative(d.lastSeenAt)}`
                : t('devicesPrinting:devices.detail.never')}
            </Row>
            <Row label={t('devicesPrinting:devices.columns.lastUser')}>{d.lastUserName ?? '—'}</Row>

            <div className="flex items-end gap-2">
              <FormField
                label={t('devicesPrinting:devices.form.name')}
                value={name}
                placeholder={t('devicesPrinting:devices.form.namePlaceholder')}
                onChange={(event) => {
                  setName(event.target.value);
                }}
              />
              <Button
                variant="secondary"
                icon={<Tag />}
                loading={save.isPending}
                disabled={name.trim().length === 0 || name === d.name}
                onClick={() => {
                  save.mutate({ name: name.trim() });
                }}
              >
                {t('common:actions.save')}
              </Button>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="font-display text-base font-semibold text-ink">
              {t('devicesPrinting:devices.detail.heartbeat')}
            </h2>
          </CardHeader>
          <CardBody className="flex flex-col gap-4">
            {d.lastSeenAt === null ? (
              <p className="text-sm text-ink-muted">{t('devicesPrinting:devices.detail.noHeartbeat')}</p>
            ) : (
              <>
                <Row label={t('devicesPrinting:devices.columns.battery')}>
                  <BatteryCell pct={d.batteryPct} />
                </Row>
                <Row label={t('devicesPrinting:devices.columns.wifi')}>
                  {d.wifiSsid ? `${d.wifiSsid}${d.wifiRssi === null ? '' : ` · ${d.wifiRssi} dBm`}` : '—'}
                </Row>
                <Row label={t('devicesPrinting:devices.columns.appVersion')}>{d.appVersion ?? '—'}</Row>
                <Row label={t('devicesPrinting:devices.columns.outbox')}>
                  {formatInteger(d.outboxDepth)}
                </Row>
                <Row label={t('devicesPrinting:devices.columns.snapshot')}>
                  {d.snapshotAgeS === null ? '—' : `${Math.round(d.snapshotAgeS / 60)} min`}
                </Row>
                <Row label={t('devicesPrinting:devices.columns.storage')}>
                  {d.storageFreeMb === null ? '—' : `${formatInteger(d.storageFreeMb)} MB`}
                </Row>
              </>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="font-display text-base font-semibold text-ink">
              {t('devicesPrinting:devices.detail.lpnLease')}
            </h2>
          </CardHeader>
          <CardBody className="flex flex-col gap-3">
            {d.lpnRangeStart === null ? (
              <p className="text-sm text-ink-muted">{t('devicesPrinting:devices.detail.lpnNever')}</p>
            ) : (
              <>
                <p className="font-mono text-sm text-ink">
                  {t('devicesPrinting:devices.detail.lpnRange', {
                    start: d.lpnRangeStart,
                    end: d.lpnRangeEnd,
                  })}
                </p>
                <p className={lpnLow ? 'text-sm text-amber-600' : 'text-sm text-ink-muted'}>
                  {t('devicesPrinting:devices.detail.lpnRemaining', { count: d.lpnRemaining ?? 0 })}
                </p>
                <p className="text-xs text-ink-muted">{formatDateTime(d.lpnLeasedAt)}</p>
              </>
            )}
            <div>
              <Button
                variant="secondary"
                loading={lease.isPending}
                onClick={() => {
                  void (async () => {
                    const ok = await confirm({
                      title: t('devicesPrinting:devices.confirm.leaseTitle'),
                      message: t('devicesPrinting:devices.confirm.leaseMessage'),
                      confirmLabel: t('devicesPrinting:devices.actions.leaseLpn'),
                      variant: 'warning',
                    });
                    if (ok) lease.mutate();
                  })();
                }}
              >
                {t('devicesPrinting:devices.actions.leaseLpn')}
              </Button>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="font-display text-base font-semibold text-ink">
              {t('devicesPrinting:devices.detail.history')}
            </h2>
          </CardHeader>
          <CardBody>
            {(history.data?.items.length ?? 0) === 0 ? (
              <p className="text-sm text-ink-muted">{t('devicesPrinting:devices.detail.noHistory')}</p>
            ) : (
              <Surface variant="subtle" className="max-h-80 overflow-y-auto p-3 scrollbar-thin">
                <ul className="flex flex-col gap-2 text-xs">
                  {history.data?.items.map((entry) => (
                    <li key={entry.id} className="flex items-baseline justify-between gap-3">
                      <span className="font-mono text-ink">{entry.action}</span>
                      <span className="shrink-0 text-ink-muted">
                        {entry.userName ? `${entry.userName} · ` : ''}
                        {formatDateTime(entry.at)}
                      </span>
                    </li>
                  ))}
                </ul>
              </Surface>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="font-mono text-xs uppercase tracking-[0.14em] text-ink-muted">{label}</span>
      <span className="text-sm text-ink">{children}</span>
    </div>
  );
}
